import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager";

//STATE INITIAL
const INIT_STATE = {
  streamRequestId: null,
  streamSessionURL: null,
  message: null,
  isExistingSession: false,
  error: null,
  loading: false,
};

const route = "core/components/LiveMetricsVideo/redux/slice";

// Start Light Metrics Live Streaming
export const startLightMetricsLiveStreaming = createAsyncThunk(
  "lightMetrics/startStreaming",
  async ({ deviceId }, { rejectWithValue, dispatch }) => {
    try {
      const data = await clientMutation(
        `mutation startLightMetricsLiveStreaming ($deviceId: String!) {
          startLightMetricsLiveStreaming (deviceId: $deviceId) {
            streamRequestId
            streamSessionURL
            message
            isExistingSession
          }
        }`,
        {
          deviceId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.startLightMetricsLiveStreaming;
    } catch (exc) {
      ExceptionManager(exc, route, "lightMetrics/startStreaming");
      return rejectWithValue(exc);
    }
  }
);

// Stop Light Metrics Live Streaming
export const stopLightMetricsLiveStreaming = createAsyncThunk(
  "lightMetrics/stopStreaming",
  async ({ deviceId, streamRequestId }, { rejectWithValue, dispatch }) => {
    try {
      const data = await clientMutation(
        `mutation stopLightMetricsLiveStreaming ($deviceId: String!, $streamRequestId: String!) {
          stopLightMetricsLiveStreaming (deviceId: $deviceId, streamRequestId: $streamRequestId) {
            streamRequestId
            streamSessionURL
            message
          }
        }`,
        {
          deviceId,
          streamRequestId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.stopLightMetricsLiveStreaming;
    } catch (exc) {
      ExceptionManager(exc, route, "lightMetrics/stopStreaming");
      return rejectWithValue(exc);
    }
  }
);

// Get Light Metrics Live Streaming Status
export const getLightMetricsLiveStreamingStatus = createAsyncThunk(
  "lightMetrics/getStatus",
  async ({ deviceId, streamRequestId }, { rejectWithValue, dispatch }) => {
    try {
      const data = await clientQuery(
        `query getLightMetricsLiveStreamingStatus ($deviceId: String!, $streamRequestId: String!) {
          getLightMetricsLiveStreamingStatus (deviceId: $deviceId, streamRequestId: $streamRequestId) {
            streamRequestId
            streamSessionURL
            message
          }
        }`,
        {
          deviceId,
          streamRequestId,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      return data.getLightMetricsLiveStreamingStatus;
    } catch (exc) {
      ExceptionManager(exc, route, "lightMetrics/getStatus");
      return rejectWithValue(exc);
    }
  }
);

//Slice
const lightMetricsRedux = createSlice({
  name: "lightMetrics",
  initialState: INIT_STATE,
  reducers: {
    resetLightMetrics(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    }
  },
  extraReducers: (builder) => {
    // startLightMetricsLiveStreaming
    builder.addCase(startLightMetricsLiveStreaming.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = null;
      state.isExistingSession = false;
    });
    builder.addCase(startLightMetricsLiveStreaming.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.streamRequestId = action.payload.streamRequestId;
      state.streamSessionURL = action.payload.streamSessionURL;
      state.message = action.payload.message;
      state.isExistingSession = !!action.payload.isExistingSession;
    });
    builder.addCase(startLightMetricsLiveStreaming.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = action.payload;
      state.isExistingSession = false;
    });

    // stopLightMetricsLiveStreaming
    builder.addCase(stopLightMetricsLiveStreaming.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = null;
    });
    builder.addCase(stopLightMetricsLiveStreaming.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.streamRequestId = action.payload.streamRequestId;
      state.streamSessionURL = action.payload.streamSessionURL;
      state.message = action.payload.message;
    });
    builder.addCase(stopLightMetricsLiveStreaming.rejected, (state, action) => {
      state.stopStreamingLoading = false;
      state.error = true;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = action.payload;
    });

    // getLightMetricsLiveStreamingStatus
    builder.addCase(getLightMetricsLiveStreamingStatus.pending, (state, action) => {
      state.loading = true;
      state.error = false;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = null;
    });
    builder.addCase(getLightMetricsLiveStreamingStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.streamRequestId = action.payload.streamRequestId;
      state.streamSessionURL = action.payload.streamSessionURL;
      state.message = action.payload.message;
    });
    builder.addCase(getLightMetricsLiveStreamingStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.streamRequestId = null;
      state.streamSessionURL = null;
      state.message = action.payload;
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = lightMetricsRedux;

// Extract and export each action creator by name
export const {
  resetLightMetrics
} = actions;

// Export the reducer, either as a default or named export
export default reducer;
